/**
 * Subsets of the enumerations packages/schema defines.
 *
 * Shapes are defined once and generated into `@scenix/core` (ADR 000020), and
 * a surface that offers part of an enumeration used to write the part out as a
 * fresh list. A fresh list catches a value being removed only if it is
 * annotated with the generated type, and catches a value being added never: a
 * sixth asset kind is a valid `AssetKind[]` without being in any of them, so
 * the schema, the API and the build all agree while the page quietly leaves the
 * new kind out (issue #358).
 *
 * Written as an exclusion instead, a subset is the enumeration minus what it
 * says it drops, so a new value joins it by default and the reason the excluded
 * ones are excluded sits beside the exclusion rather than in nobody's head.
 */

/**
 * Every value of `options` except those in `without`, in the enumeration's own
 * order.
 *
 * `NoInfer` keeps `without` from widening the parameter: without it,
 * `allBut(AssetKindSchema.options, ["document", "typo"])` would infer a union
 * containing `"typo"` and compile, which is the drift this is here to stop.
 */
export function allBut<T extends string>(
  options: readonly T[],
  without: readonly NoInfer<T>[],
): T[] {
  for (const value of without) {
    // Unreachable in a build, since `without` is typed against the enumeration
    // and `tsc` runs before `vite build`. It is here for the case the type
    // checker cannot see: a list read at run time, and a test that passes one.
    if (!options.includes(value)) {
      throw new Error(`allBut excludes ${JSON.stringify(value)}, which is not one of the options`);
    }
  }
  return options.filter((option) => !without.includes(option));
}
